import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { PenTool, Loader2, Lock, Sparkles, ThumbsUp, Target, Award, History, FileText } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { UpgradeGate } from "@/components/UpgradeGate";

const TYPES = ["AP Free-Response", "SAT Essay"];

export function EssayLab({ onAuth, onNavigate }) {
  const { user } = useAuth();
  const [essayType, setEssayType] = useState("SAT Essay");
  const [prompt, setPrompt] = useState("");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const words = text.trim() ? text.trim().split(/\s+/).length : 0;

  const loadHistory = () => api.get("/essay/history")
    .then(({ data }) => setHistory(data)).catch(() => {});

  useEffect(() => { if (user) loadHistory(); }, [user]);

  const critique = async (e) => {
    e.preventDefault();
    if (words < 40) return toast.error("Paste at least ~40 words for a meaningful critique.");
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/essay/critique", { essay_type: essayType, prompt, essay_text: text });
      setResult(data);
      toast.success(`Scored ${data.overall_score}/${data.max_score}.`);
      loadHistory();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <div className="h-14 w-14 rounded-xl bg-navy-soft flex items-center justify-center mx-auto mb-6">
          <Lock className="h-6 w-6 text-navy" />
        </div>
        <h2 className="font-serif text-3xl font-semibold text-navy">Sign in to use the Essay Lab</h2>
        <p className="text-slate-600 mt-3">Submit AP free-response and SAT essays for a rubric-scored critique.</p>
        <Button onClick={() => onAuth("login")} data-testid="essay-auth-cta"
          className="mt-6 bg-navy hover:bg-navy-light text-white">Sign in / Enrol</Button>
      </div>
    );
  }

  if (!user.premium && user.role !== "admin") {
    return <UpgradeGate feature="the Essay Lab" onUpgrade={() => onNavigate("home")} />;
  }

  const pct = result ? Math.min(100, Math.max(0, Math.round((result.overall_score / result.max_score) * 100))) : 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-8">
        <span className="text-xs font-mono uppercase tracking-[0.2em] text-persian-blue font-semibold">Writing</span>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-navy mt-2">Essay Lab</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">Claude critiques your AP free-response or SAT essay against the official rubric—scores each dimension and tells you exactly what to sharpen.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <form onSubmit={critique} className="lg:col-span-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Essay type</Label>
              <Select value={essayType} onValueChange={setEssayType}>
                <SelectTrigger data-testid="essay-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Prompt / assignment (optional)</Label>
              <Input value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="essay-prompt-input"
                placeholder="e.g. Analyze the author's rhetoric" />
            </div>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Your essay</Label>
              <span className="text-xs text-slate-400 font-mono">{words} words</span>
            </div>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={16}
              data-testid="essay-text-textarea" placeholder="Paste your full essay here…"
              className="resize-none leading-relaxed" />
          </div>
          <Button type="submit" disabled={loading} data-testid="essay-submit-button"
            className="w-full bg-persian-rose hover:bg-persian-rosehover text-white font-semibold">
            {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Grading…</> : <><Sparkles className="h-4 w-4 mr-2" />Critique my essay</>}
          </Button>
        </form>

        <div className="lg:col-span-6">
          {!result && !loading && (
            <div className="h-full min-h-[300px] rounded-2xl border border-dashed border-slate-300 flex flex-col items-center justify-center text-center p-8">
              <PenTool className="h-10 w-10 text-slate-300 mb-3" />
              <p className="text-slate-500 text-sm max-w-xs">Your rubric breakdown, strengths, and targeted improvements will appear here.</p>
            </div>
          )}
          {loading && (
            <div className="h-full min-h-[300px] rounded-2xl bg-navy-soft flex flex-col items-center justify-center">
              <Loader2 className="h-8 w-8 text-persian-blue animate-spin" />
              <p className="text-navy mt-3 text-sm">Examining against the rubric…</p>
            </div>
          )}
          {result && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              data-testid="essay-result" className="space-y-4">
              <div className="bg-navy rounded-2xl p-6 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Award className="h-5 w-5 text-persian-blue" />
                    <span className="font-serif text-xl">Overall</span>
                  </div>
                  <span className="font-serif text-3xl font-bold">
                    {result.overall_score}<span className="text-slate-400 text-lg">/{result.max_score}</span>
                  </span>
                </div>
                <div className="mt-3 h-2 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full bg-persian-rose transition-all" style={{ width: `${pct}%` }} />
                </div>
                <p className="text-sm text-slate-300 mt-3 leading-relaxed">{result.summary}</p>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 space-y-4">
                {(result.criteria || []).map((c, i) => (
                  <div key={i} data-testid={`essay-criterion-${i}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-navy">{c.name}</span>
                      <span className="font-mono text-slate-500">{c.score}/{c.max}</span>
                    </div>
                    <Progress value={Math.min(100, (c.score / c.max) * 100)} className="h-1.5 mt-1.5" />
                    <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">{c.comment}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white rounded-xl border border-slate-200/80 p-5">
                  <div className="flex items-center gap-2 text-persian-blue mb-2">
                    <ThumbsUp className="h-4 w-4" /><span className="text-sm font-semibold">Strengths</span>
                  </div>
                  <ul className="space-y-1.5 text-sm text-slate-600 list-disc pl-4">
                    {(result.strengths || []).map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </div>
                <div className="bg-white rounded-xl border border-slate-200/80 p-5">
                  <div className="flex items-center gap-2 text-persian-rose mb-2">
                    <Target className="h-4 w-4" /><span className="text-sm font-semibold">Improve</span>
                  </div>
                  <ul className="space-y-1.5 text-sm text-slate-600 list-disc pl-4">
                    {(result.improvements || []).map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {history.length > 0 && (
        <div className="mt-12" data-testid="essay-history">
          <div className="flex items-center gap-2 mb-4">
            <History className="h-5 w-5 text-persian-blue" />
            <h2 className="font-serif text-2xl font-semibold text-navy">Past critiques</h2>
            <span className="text-xs text-slate-500">{history.length} saved</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {history.map((h) => {
              const r = h.result || {};
              const p = r.max_score ? Math.min(100, Math.round((r.overall_score / r.max_score) * 100)) : 0;
              return (
                <button key={h.id} data-testid={`essay-history-item-${h.id}`}
                  onClick={() => { setResult(r); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  className="text-left bg-white rounded-xl border border-slate-200/80 shadow-sm hover:shadow-md hover:border-persian-blue/50 transition-all p-5">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wide text-persian-blue">
                      <FileText className="h-3.5 w-3.5" /> {h.essay_type}
                    </span>
                    <span className="font-serif text-lg font-bold text-navy">
                      {r.overall_score}<span className="text-slate-400 text-sm">/{r.max_score}</span>
                    </span>
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-persian-rose" style={{ width: `${p}%` }} />
                  </div>
                  <p className="text-xs text-slate-500 mt-3 line-clamp-2">{h.essay_excerpt}</p>
                  <p className="text-[11px] text-slate-400 mt-2">
                    {h.created_at ? new Date(h.created_at).toLocaleString() : ""}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
